import type { Settings } from '@ais-ui/websockets';
import type {
  DomainTranslator,
  VesselCard,
  VesselSettings,
  VesselTargetType,
} from '../types';

const TARGET_TYPES: VesselTargetType[] = [
  'vessel',
  'aton',
  'virtualAton',
  'methyd',
  'baseStation',
  'radar',
  'radarFused',
  'das',
  'drone',
];

// Per-target-type defaults, aligned with the app's pre-existing legacy
// behavior (never-configured target types render as empty/collapsed, not a
// pre-populated favorites list) so reads stay byte-for-byte identical for
// phase 1, before any backend record exists.
const FAVORITE_DEFAULTS: Record<VesselTargetType, string[]> = {
  vessel: [],
  aton: [],
  virtualAton: [],
  methyd: [],
  baseStation: [],
  radar: [],
  radarFused: [],
  das: [],
  drone: [],
};

const HEADER_DEFAULTS: Record<VesselTargetType, string> = {
  vessel: 'name',
  aton: 'name',
  virtualAton: 'name',
  methyd: 'stationName',
  baseStation: 'name',
  radar: 'name',
  radarFused: 'name',
  das: 'site',
  drone: 'uasId',
};

const SUBTITLE_DEFAULTS: Record<VesselTargetType, string> = {
  vessel: 'callSign',
  aton: 'mmsi',
  virtualAton: 'mmsi',
  methyd: 'mmsi',
  baseStation: 'mmsi',
  radar: 'id',
  radarFused: 'sensorTrackId',
  das: 'trackId',
  drone: 'macAddress',
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

/**
 * Reproduces the app's legacy→per-type migration: a stored value may be the old
 * flat form (an array of favorite fields / a single header/subtitle string that
 * applied to vessels only) or the newer per-target-type object. Normalise both
 * into the per-type record the backend models.
 */
function toPerType<V>(
  stored: unknown,
  defaults: Record<VesselTargetType, V>,
  isFlat: (value: unknown) => boolean,
): Record<VesselTargetType, V> {
  if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
    return { ...defaults, ...(stored as Record<VesselTargetType, V>) };
  }
  if (isFlat(stored)) {
    return { ...defaults, vessel: stored as V };
  }
  return defaults;
}

export const vesselsTranslator: DomainTranslator<VesselSettings> = {
  fromBlob(blob: Settings): VesselSettings {
    const ribbon = asRecord(blob['ribbonPlugin.settings']);
    return {
      card: {
        // The app has always defaulted an unconfigured card to expanded, so
        // an absent key must mean "expanded" (true), not "collapsed" — unlike
        // Boolean(undefined), which would silently flip that default.
        expanded_by_default:
          ribbon.target_card_expanded_by_default === undefined
            ? true
            : Boolean(ribbon.target_card_expanded_by_default),
        favorite_fields: toPerType(
          ribbon.favorite_fields,
          FAVORITE_DEFAULTS,
          Array.isArray,
        ),
        header_field: toPerType(
          ribbon.target_header_field,
          HEADER_DEFAULTS,
          (v) => typeof v === 'string',
        ),
        subtitle_field: toPerType(
          ribbon.target_subtitle_field,
          SUBTITLE_DEFAULTS,
          (v) => typeof v === 'string',
        ),
      },
    };
  },

  applyToBlob(blob: Settings, patch: Partial<VesselSettings>): Settings {
    const next = { ...(blob as unknown as Record<string, unknown>) };
    const ribbon = { ...asRecord(next['ribbonPlugin.settings']) };
    const card = patch.card as Partial<VesselCard> | undefined;

    if (card) {
      if (card.expanded_by_default !== undefined)
        ribbon.target_card_expanded_by_default = card.expanded_by_default;
      // Write the per-type object form; the app already reads objects back.
      if (card.favorite_fields !== undefined)
        ribbon.favorite_fields = card.favorite_fields;
      if (card.header_field !== undefined)
        ribbon.target_header_field = card.header_field;
      if (card.subtitle_field !== undefined)
        ribbon.target_subtitle_field = card.subtitle_field;
    }

    next['ribbonPlugin.settings'] = ribbon;
    return next as unknown as Settings;
  },
};

export { TARGET_TYPES as vesselTargetTypes };
